/**
 * Google Analytics 4 integration with cookie consent
 *
 * GA4 laddas endast efter att användaren gett sitt samtycke.
 * Samtycket sparas i localStorage.
 */

const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;

// Cookie consent storage key
const CONSENT_KEY = "kvanten_cookie_consent";

export type ConsentStatus = "granted" | "denied" | "pending";

interface ConsentData {
  analytics: boolean;
  timestamp: string;
}

/**
 * Get current consent status from localStorage
 */
export function getConsentStatus(): ConsentStatus {
  if (typeof window === "undefined") return "pending";

  const stored = localStorage.getItem(CONSENT_KEY);
  if (!stored) return "pending";

  try {
    const data: ConsentData = JSON.parse(stored);
    return data.analytics ? "granted" : "denied";
  } catch {
    return "pending";
  }
}

/**
 * Save consent choice to localStorage
 */
export function saveConsent(analytics: boolean): void {
  const data: ConsentData = {
    analytics,
    timestamp: new Date().toISOString(),
  };
  localStorage.setItem(CONSENT_KEY, JSON.stringify(data));

  if (analytics) {
    initializeGA4();
  }
}

/**
 * Check if GA4 script is already loaded
 */
function isGA4Loaded(): boolean {
  return typeof window !== "undefined" && typeof window.gtag === "function";
}

/**
 * Initialize GA4 by injecting the script
 */
export function initializeGA4(): void {
  if (!GA_MEASUREMENT_ID) {
    console.warn("GA4 Measurement ID not configured");
    return;
  }

  if (isGA4Loaded()) {
    return;
  }

  // Create gtag script
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // Initialize dataLayer BEFORE script loads (Google's requirement)
  window.dataLayer = window.dataLayer || [];

  // IMPORTANT: Must use 'arguments' object, not rest parameters
  // Google's gtag.js expects this exact pattern
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  window.gtag = function () {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };

  window.gtag("js", new Date());
  window.gtag("config", GA_MEASUREMENT_ID, {
    anonymize_ip: true, // GDPR compliance
    cookie_flags: "SameSite=None;Secure",
  });
}

/**
 * Track a page view (call this on route changes)
 */
export function trackPageView(path: string, title?: string): void {
  if (!isGA4Loaded() || getConsentStatus() !== "granted") return;

  window.gtag("event", "page_view", {
    page_path: path,
    page_title: title || document.title,
  });
}

/**
 * Track a custom event
 */
export function trackEvent(
  eventName: string,
  params?: Record<string, unknown>
): void {
  if (!isGA4Loaded() || getConsentStatus() !== "granted") return;

  window.gtag("event", eventName, params);
}

/**
 * Initialize analytics if consent was previously granted
 * Call this once at app startup
 */
export function initializeAnalyticsIfConsented(): void {
  if (getConsentStatus() === "granted") {
    initializeGA4();
  }
}

// Type definitions for window
declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}
